import axios from '@/axiosInterceptor';

const state = () => ({
  collection: [],
  carrierList: [],
  connections: [],
  show: {},
  active_dispatchers: [],
  is_active_dispatchers_loading: false,
  active_dispatchers_pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  },
  pending_dispatchers: [],
  is_pending_dispatchers_loading: false,
  pending_dispatchers_pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  },
});

const getters = {
  pendingConnectionsCount: (state) => {
    const pendingConnections = state.collection.filter(
      (item) => item.status === 'pending'
    );
    if (!pendingConnections || pendingConnections.length < 1) return 0;
    return pendingConnections.length;
  },
};

// actions

const actions = {
  async getDispatcherConnections({ commit }, query) {
    try {
      let res = await axios.get(
        process.env.API_IDENTITY + '/api/v1/dispatcher_connections',
        { q: query }
      );
      commit('setCollection', res.data);
      return res.data;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getActiveDispatcherConnections(
    { commit, state },
    { page = 1, per_page = 50, quiet = false, refetch = false }
  ) {
    if (!quiet) {
      commit('setActiveDispatchersLoading', true);
    }

    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/dispatcher_connections?page=${page}&per_page=${per_page}&q[status_eq]=active`
      );

      if (refetch) {
        const combinedDispatchers = [
          ...(state.active_dispatchers || []),
          ...res.data,
        ];
        commit('setActiveDispatchers', combinedDispatchers);
      } else {
        commit('setActiveDispatchers', res.data);
      }
      commit('setActiveDispatchersPagination', {
        totalRecords: res.headers?.totalitems,
        totalPages: res.headers?.totalpages,
        currentPage: res.headers?.currentpage,
      });

      return res.data;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setActiveDispatchersLoading', false);
    }
  },
  async getPendingDispatcherConnections(
    { commit, state },
    { page = 1, per_page = 50, quiet = false, refetch = false }
  ) {
    if (!quiet) {
      commit('setPendingDispatchersLoading', true);
    }

    try {
      let res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/dispatcher_connections?page=${page}&per_page=${per_page}&q[status_not_eq]=active`
      );
      if (refetch) {
        const combinedDispatchers = [
          ...(state.pending_dispatchers || []),
          ...res.data,
        ];

        commit('setPendingDispatchers', combinedDispatchers);
      } else {
        commit('setPendingDispatchers', res.data);
      }
      commit('setPendingDispatchersPagination', {
        totalRecords: res.headers?.totalitems,
        totalPages: res.headers?.totalpages,
        currentPage: res.headers?.currentpage,
      });

      return res.data;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    } finally {
      commit('setPendingDispatchersLoading', false);
    }
  },
  async getDispatcherConnection({ commit }, id) {
    try {
      let res = await axios.get(
        process.env.API_IDENTITY + '/api/v1/dispatcher_connections/' + id
      );
      commit('setShow', res.data);
      return res.data;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async activateDispatcherConnection({ state, commit }, id) {
    try {
      let res = await axios.patch(
        process.env.API_IDENTITY +
          '/api/v1/dispatcher_connections/' +
          id +
          '/activate'
      );
      let newCollection = state.collection.map((el) => {
        return el.id === res.data.id ? res.data : el;
      });
      commit('setCollection', newCollection);
      commit('setShow', res.data);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async inactivateDispatcherConnection({ commit, state }, id) {
    try {
      let res = await axios.patch(
        process.env.API_IDENTITY +
          '/api/v1/dispatcher_connections/' +
          id +
          '/inactivate'
      );
      let newCollection = state.collection.map((el) => {
        return el.id === res.data.id ? res.data : el;
      });
      commit('setCollection', newCollection);
      commit('setShow', res.data);
      return res;
    } catch (e) {
      let status = e.response?.status;
      if (
        axios.isAxiosError(e) &&
        status &&
        [401, 403, 404, 422].includes(status)
      ) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
  async getListOfCarriers({ commit }) {
    const buildParams = `page=1&per_page=50&q[s]=id desc`;
    try {
      const res = await axios.get(
        process.env.API_IDENTITY +
          '/api/v1/dispatcher_connections?' +
          buildParams
      );
      if ([200, 201].includes(res.status)) {
        commit('setConnections', res.data);
        const carrierList = res.data.map((item) => item.carrier);
        commit('setCarriersData', carrierList);
        return carrierList;
      }
      return [];
    } catch (e) {
      if (axios.isAxiosError(e) && [403, 422].includes(e?.response?.status)) {
        return e.response;
      } else {
        throw e;
      }
    }
  },
};

//mutations
const mutations = {
  setCollection(state, data) {
    state.collection = data;
  },
  setShow(state, data) {
    state.show = data;
  },
  setCarriersData(state, carrierList) {
    state.carrierList = carrierList;
  },
  setConnections(state, connectionList) {
    state.connections = connectionList;
  },
  setActiveDispatchers(state, data) {
    state.active_dispatchers = data;
  },
  setActiveDispatchersLoading(state, bool) {
    state.is_active_dispatchers_loading = bool;
  },
  setActiveDispatchersPagination(state, data) {
    state.active_dispatchers_pagination = data;
  },
  setPendingDispatchers(state, data) {
    state.pending_dispatchers = data;
  },
  setPendingDispatchersLoading(state, bool) {
    state.is_pending_dispatchers_loading = bool;
  },
  setPendingDispatchersPagination(state, data) {
    state.pending_dispatchers_pagination = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
